import {
  AlertTriangle,
  TrendingDown,
  Calendar,
  ArrowRight,
  Sparkles,
  Activity,
  Package,
  BarChart3,
  Clock,
  Filter,
  ChartBar,
  AlertCircle,
  FileSearch,
  Target,
  Zap
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from 'lib/utils';

export function InitialScreen() {
  return (
    <div className="p-8">
      <div className="flex min-h-[65vh] w-full flex-col items-center justify-center">
        {/* Main Content Container */}
        <div className="relative max-w-4xl w-full">
          {/* Background Decorations */}
          <div className="absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br from-red-500/10 to-transparent rounded-full blur-3xl animate-pulse" />
          <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-gradient-to-tr from-orange-500/10 to-transparent rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-amber-500/5 via-red-500/5 to-orange-500/5 rounded-full blur-3xl" />
          
          {/* Central Icon Showcase */}
          <div className="relative mb-10">
            <div className="flex justify-center">
              <div className="relative">
                {/* Animated Icon Container */}
                <div className={cn(
                  "relative p-8 rounded-3xl",
                  "bg-gradient-to-br from-red-600 via-orange-600 to-amber-600",
                  "shadow-2xl shadow-red-500/30 dark:shadow-red-500/20",
                  "group"
                )}>
                  {/* Main Icon */}
                  <div className="relative">
                    <FileSearch className="h-20 w-20 text-white animate-pulse" />
                    
                    {/* Orbiting Icons */}
                    <div className="absolute -top-3 -right-3 p-2 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 shadow-lg animate-bounce">
                      <AlertTriangle className="h-4 w-4 text-white" />
                    </div>
                    <div className="absolute -bottom-3 -left-3 p-2 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-lg animate-bounce delay-150">
                      <Package className="h-4 w-4 text-white" />
                    </div>
                    <div className="absolute -top-3 -left-3 p-2 rounded-xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg animate-bounce delay-300">
                      <Target className="h-4 w-4 text-white" />
                    </div>
                    <div className="absolute -bottom-3 -right-3 p-2 rounded-xl bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg animate-bounce delay-500">
                      <Zap className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  
                  {/* Sparkles */}
                  <Sparkles className="absolute top-2 right-2 h-6 w-6 text-yellow-300 animate-pulse" />
                  <Sparkles className="absolute bottom-2 left-2 h-5 w-5 text-yellow-300 animate-pulse delay-300" />
                  
                  {/* Glow Effect */}
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
                
                {/* Decorative Rings */}
                <div className="absolute inset-0 rounded-3xl border-2 border-red-500/20 scale-110 animate-ping" />
                <div className="absolute inset-0 rounded-3xl border-2 border-orange-500/20 scale-125 animate-ping delay-200" />
                <div className="absolute inset-0 rounded-3xl border-2 border-amber-500/20 scale-150 animate-ping delay-500" />
              </div>
            </div>
          </div>

          {/* Title and Description */}
          <div className="text-center space-y-4">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-red-700 to-orange-700 dark:from-red-400 dark:to-orange-400 bg-clip-text text-transparent">
              تقرير نواقص الأصناف
            </h2>
            <p className="text-base text-muted-foreground max-w-md mx-auto">
              قم بتحديد عدد الأيام لتحليل البيانات وعرض الأصناف التي تحتاج لمتابعة
            </p>
          </div>

          {/* Feature Cards Grid */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Critical Items Card */}
            <Card className={cn(
              "relative p-5 border-0 overflow-hidden",
              "bg-gradient-to-br from-red-50 via-red-50/50 to-transparent",
              "dark:from-red-950/40 dark:via-red-950/20 dark:to-transparent",
              "shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer group"
            )}>
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/20 to-transparent rounded-full blur-2xl" />
              <div className="relative flex flex-col space-y-3">
                <div className="flex items-center justify-between">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-red-600 to-red-700 text-white shadow-lg group-hover:scale-110 transition-transform">
                    <AlertTriangle className="h-5 w-5" />
                  </div>
                  <Badge className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-0">
                    حرج
                  </Badge>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-red-700 dark:text-red-400">
                    أصناف حرجة
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    رصد الأصناف التي تحتاج تدخل فوري
                  </p>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-red-600 dark:text-red-400">
                  <AlertCircle className="h-3 w-3" />
                  <span>مراقبة مستمرة</span>
                </div>
              </div>
            </Card>

            {/* Trend Analysis Card */}
            <Card className={cn(
              "relative p-5 border-0 overflow-hidden",
              "bg-gradient-to-br from-orange-50 via-orange-50/50 to-transparent",
              "dark:from-orange-950/40 dark:via-orange-950/20 dark:to-transparent",
              "shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer group"
            )}>
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/20 to-transparent rounded-full blur-2xl" />
              <div className="relative flex flex-col space-y-3">
                <div className="flex items-center justify-between">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-orange-600 to-orange-700 text-white shadow-lg group-hover:scale-110 transition-transform">
                    <TrendingDown className="h-5 w-5" />
                  </div>
                  <Badge className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-0">
                    تحليل
                  </Badge>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-orange-700 dark:text-orange-400">
                    تحليل الاتجاهات
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    متابعة حركة المخزون والتنبؤ
                  </p>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-orange-600 dark:text-orange-400">
                  <BarChart3 className="h-3 w-3" />
                  <span>إحصائيات دقيقة</span>
                </div>
              </div>
            </Card>

            {/* Market Offers Card */}
            <Card className={cn(
              "relative p-5 border-0 overflow-hidden",
              "bg-gradient-to-br from-amber-50 via-amber-50/50 to-transparent",
              "dark:from-amber-950/40 dark:via-amber-950/20 dark:to-transparent",
              "shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer group"
            )}>
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-amber-500/20 to-transparent rounded-full blur-2xl" />
              <div className="relative flex flex-col space-y-3">
                <div className="flex items-center justify-between">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-600 to-amber-700 text-white shadow-lg group-hover:scale-110 transition-transform">
                    <Activity className="h-5 w-5" />
                  </div>
                  <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-0">
                    عروض
                  </Badge>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-amber-700 dark:text-amber-400">
                    عروض السوق
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    أفضل الخصومات والعروض المتاحة
                  </p>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-400">
                  <Zap className="h-3 w-3" />
                  <span>تحديث يومي</span>
                </div>
              </div>
            </Card>

            {/* Time Filter Card */}
            <Card className={cn(
              "relative p-5 border-0 overflow-hidden",
              "bg-gradient-to-br from-purple-50 via-purple-50/50 to-transparent",
              "dark:from-purple-950/40 dark:via-purple-950/20 dark:to-transparent",
              "shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer group"
            )}>
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/20 to-transparent rounded-full blur-2xl" />
              <div className="relative flex flex-col space-y-3">
                <div className="flex items-center justify-between">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 text-white shadow-lg group-hover:scale-110 transition-transform">
                    <Clock className="h-5 w-5" />
                  </div>
                  <Badge className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-0">
                    زمني
                  </Badge>
                </div>
                <div>
                  <h4 className="text-sm font-bold text-purple-700 dark:text-purple-400">
                    فترة التحليل
                  </h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    تحديد نطاق زمني مخصص للتقرير
                  </p>
                </div>
                <div className="flex items-center gap-1 text-[10px] text-purple-600 dark:text-purple-400">
                  <Filter className="h-3 w-3" />
                  <span>فلترة مرنة</span>
                </div>
              </div>
            </Card>
          </div>

          {/* Call to Action */}
          <div className="mt-10 flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-full blur-xl animate-pulse" />
              <div className={cn(
                "relative flex items-center gap-3 px-6 py-3 rounded-full",
                "bg-gradient-to-r from-red-600 via-orange-600 to-amber-600",
                "text-white font-medium shadow-xl",
                "hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer group"
              )}>
                <Calendar className="h-5 w-5 group-hover:rotate-12 transition-transform" />
                <span className="text-sm font-semibold">
                  حدد عدد الأيام للبدء في التحليل
                </span>
                <ArrowRight className="h-5 w-5 animate-pulse group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
            
            {/* Stats Preview */}
            <div className="flex items-center gap-6 text-xs text-muted-foreground">
              <div className="flex items-center gap-1">
                <ChartBar className="h-3.5 w-3.5" />
                <span>تقرير شامل</span>
              </div>
              <div className="h-4 w-px bg-border" />
              <div className="flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                <span>تحديث فوري</span>
              </div>
              <div className="h-4 w-px bg-border" />
              <div className="flex items-center gap-1">
                <Target className="h-3.5 w-3.5" />
                <span>دقة عالية</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
